import React from "react";
import { motion } from "framer-motion";
import { BookOpen, ClipboardCheck, Lightbulb, GraduationCap } from "lucide-react";
import { ACADEMIC_STAGES, IMAGES } from "@/lib/constants";
import { PageBanner } from "@/components/PageBanner";
import { SectionHeading } from "@/components/SectionHeading";
import { Badge } from "@/components/ui/badge";

export default function Academics() {
  return (
    <div>
      <PageBanner title="Academics" subtitle="A structured, joyful learning path from Pre-Nursery to Class 10 under the CBSE curriculum." image={IMAGES.smartClass} />

      <section className="max-w-7xl mx-auto px-6 py-20 space-y-10">
        {ACADEMIC_STAGES.map((s, idx) => (
          <motion.div key={s.key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="grid lg:grid-cols-3 gap-8 items-start bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
            <div>
              <div className="h-1.5 w-16 rounded-full mb-4" style={{ backgroundColor: s.color }} />
              <h2 className="font-head font-extrabold text-3xl text-navy">{s.name}</h2>
              <p className="font-semibold mt-1" style={{ color: s.color }}>{s.range}</p>
              <p className="text-slate-600 mt-4 leading-relaxed text-sm">{s.desc}</p>
              <div className="flex flex-wrap gap-2 mt-5">
                {s.classes.map((c) => (
                  <span key={c} className="text-xs font-semibold px-3 py-1 rounded-full text-navy" style={{ backgroundColor: `${s.color}22` }}>{c}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2">
              <h4 className="font-head font-bold text-navy mb-3 flex items-center gap-2"><BookOpen className="h-5 w-5 text-brandred" /> Subjects Offered</h4>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {s.subjects.map((sub) => (
                  <div key={sub} className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
                    <GraduationCap className="h-4 w-4 shrink-0" style={{ color: s.color }} /> {sub}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Teaching & Exams */}
      <section className="bg-[#F3F4F6] py-20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm">
            <div className="h-14 w-14 rounded-2xl bg-gold/20 text-gold grid place-items-center mb-5"><Lightbulb className="h-7 w-7" /></div>
            <SectionHeading title="Our Teaching Method" />
            <ul className="space-y-3 text-slate-600 text-sm">
              {["Activity & play-based learning for early years", "Interactive smart-class digital lessons", "Concept-first, application-driven teaching", "Regular practicals in science & computer labs", "Continuous doubt-clearing with individual attention"].map((t) => (
                <li key={t} className="flex gap-2"><span className="text-brandred font-bold">✓</span> {t}</li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm">
            <div className="h-14 w-14 rounded-2xl bg-brandteal/20 text-brandteal grid place-items-center mb-5"><ClipboardCheck className="h-7 w-7" /></div>
            <SectionHeading title="Examination System" />
            <ul className="space-y-3 text-slate-600 text-sm">
              {["Continuous & Comprehensive Evaluation (CCE)", "Periodic unit tests and class assessments", "Half-yearly and annual examinations", "Practical & project-based evaluation", "Detailed progress reports shared with parents"].map((t) => (
                <li key={t} className="flex gap-2"><span className="text-brandteal font-bold">✓</span> {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
